import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { EditionProvider } from './lib/edition-context';
import { I18nProvider } from './hooks/use-i18n';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import { ErrorBoundary } from './components/ErrorBoundary';
import './App.css';

// Public Pages
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import ArticlePage from './pages/ArticlePage';
import SearchPage from './pages/SearchPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminArticles from './pages/admin/AdminArticles';
import AdminArticleEditor from './pages/admin/AdminArticleEditor';
import AdminRSSFeeds from './pages/admin/AdminRSSFeeds';
import AdminAutoPublish from './pages/admin/AdminAutoPublish';
import AdminBreakingNews from './pages/admin/AdminBreakingNews';
import AdminComments from './pages/admin/AdminComments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminHR from './pages/admin/AdminHR';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminAudit from './pages/admin/AdminAudit';
import AdminSettings from './pages/admin/AdminSettings';
import AdminMedia from './pages/admin/AdminMedia';
import AdminLiveBlog from './pages/admin/AdminLiveBlog';
import AdminPolls from './pages/admin/AdminPolls';
import AdminReels from './pages/admin/AdminReels';
import AdminLiveStreams from './pages/admin/AdminLiveStreams';
import AdminPodcasts from './pages/admin/AdminPodcasts';
import AdminAds from './pages/admin/AdminAds';
import AdminTemplates from './pages/admin/AdminTemplates';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminLeave from './pages/admin/AdminLeave';
import AdminReviews from './pages/admin/AdminReviews';
import AdminSEO from './pages/admin/AdminSEO';
import AdminCommissions from './pages/admin/AdminCommissions';
import AdminFreelancers from './pages/admin/AdminFreelancers';
import AdminPlanning from './pages/admin/AdminPlanning';
import AdminSupport from './pages/admin/AdminSupport';
import AdminRecruitment from './pages/admin/AdminRecruitment';
import AdminFinance from './pages/admin/AdminFinance';

// V3 Admin Pages
import AdminMarketing from './pages/admin/AdminMarketing';
import AdminEquipment from './pages/admin/AdminEquipment';
import AdminSources from './pages/admin/AdminSources';
import AdminShifts from './pages/admin/AdminShifts';
import AdminGDPR from './pages/admin/AdminGDPR';
import AdminUGC from './pages/admin/AdminUGC';
import AdminOrgChart from './pages/admin/AdminOrgChart';
import AdminSEOAdvanced from './pages/admin/AdminSEOAdvanced';
import AdminSeries from './pages/admin/AdminSeries';
import AdminEngagement from './pages/admin/AdminEngagement';
import AdminVocabulary from './pages/admin/AdminVocabulary';
import AdminCategories from './pages/admin/AdminCategories';
import AdminWiki from './pages/admin/AdminWiki';
import AdminNewsletterBuilder from './pages/admin/AdminNewsletterBuilder';
import AdminWebVitals from './pages/admin/AdminWebVitals';
import AdminSocialMedia from './pages/admin/AdminSocialMedia';
import AdminTimesheets from './pages/admin/AdminTimesheets';
import AdminOKRs from './pages/admin/AdminOKRs';
import AdminCompetitors from './pages/admin/AdminCompetitors';
import AdminSustainability from './pages/admin/AdminSustainability';
import AdminGDPRFull from './pages/admin/AdminGDPRFull';
import AdminFinanceFull from './pages/admin/AdminFinanceFull';
import AdminLiveData from './pages/admin/AdminLiveData';
import AdminFieldOps from './pages/admin/AdminFieldOps';
import AdminPayroll from './pages/admin/AdminPayroll';
import AdminEcommerce from './pages/admin/AdminEcommerce';
import AdminLMS from './pages/admin/AdminLMS';
import AdminContracts from './pages/admin/AdminContracts';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import SmartTVPage from './pages/SmartTVPage';
import AdminAnalyticsStream from './pages/admin/AdminAnalyticsStream';
import AdminSystemDR from './pages/admin/AdminSystemDR';
// V7 Admin Pages
import AdminEntitlements from './pages/admin/AdminEntitlements';
import AdminCorporateSeats from './pages/admin/AdminCorporateSeats';
import AdminHeadlineExperiments from './pages/admin/AdminHeadlineExperiments';
import AdminAIGovernance from './pages/admin/AdminAIGovernance';
import AdminAccountSecurity from './pages/admin/AdminAccountSecurity';
import AdminGovernance from './pages/admin/AdminGovernance';
import AdminWireIngestion from './pages/admin/AdminWireIngestion';
import AdminSocialPublishing from './pages/admin/AdminSocialPublishing';
import AdminElections from './pages/admin/AdminElections';
import { LiveChatWidget } from './components/LiveChatWidget';

// Public Pages (extended)
import AuthorPage from './pages/AuthorPage';
import AuthorsPage from './pages/AuthorsPage';
import BookmarksPage from './pages/BookmarksPage';
import CareersPage from './pages/CareersPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import TagPage from './pages/TagPage';
import LiveBlogPage from './pages/LiveBlogPage';
import NotFoundPage from './pages/NotFoundPage';
import EmbedGeneratorPage from './pages/EmbedGeneratorPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = ['super_admin', 'admin', 'editor', 'journalist', 'moderator'];
  if (!allowed.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <EditionProvider>
      <I18nProvider>
      <Router>
        <div className="app-container">
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/p/:slug" element={<ArticlePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/embed" element={<EmbedGeneratorPage />} />
            <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
            <Route path="/tv" element={<SmartTVPage />} />
            <Route path="/author/:id" element={<AuthorPage />} />
            <Route path="/authors" element={<AuthorsPage />} />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/tag/:tag" element={<TagPage />} />
            <Route path="/live/:id" element={<LiveBlogPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/new" element={<AdminArticleEditor />} />
              <Route path="articles/:id/edit" element={<AdminArticleEditor />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="templates" element={<AdminTemplates />} />
              <Route path="rss-feeds" element={<AdminRSSFeeds />} />
              <Route path="auto-publish" element={<AdminAutoPublish />} />
              <Route path="breaking-news" element={<AdminBreakingNews />} />
              <Route path="live-blog" element={<AdminLiveBlog />} />
              <Route path="polls" element={<AdminPolls />} />
              <Route path="reels" element={<AdminReels />} />
              <Route path="live-streams" element={<AdminLiveStreams />} />
              <Route path="podcasts" element={<AdminPodcasts />} />
              <Route path="comments" element={<AdminComments />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="hr" element={<AdminHR />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="leave" element={<AdminLeave />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="seo" element={<AdminSEO />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="freelancers" element={<AdminFreelancers />} />
              <Route path="planning" element={<AdminPlanning />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="recruitment" element={<AdminRecruitment />} />
              <Route path="finance" element={<AdminFinance />} />
              {/* V3 Routes */}
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="equipment" element={<AdminEquipment />} />
              <Route path="sources" element={<AdminSources />} />
              <Route path="shifts" element={<AdminShifts />} />
              <Route path="gdpr" element={<AdminGDPR />} />
              <Route path="ugc" element={<AdminUGC />} />
              <Route path="org-chart" element={<AdminOrgChart />} />
              <Route path="seo-advanced" element={<AdminSEOAdvanced />} />
              <Route path="series" element={<AdminSeries />} />
              <Route path="engagement" element={<AdminEngagement />} />
              <Route path="vocabulary" element={<AdminVocabulary />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="wiki" element={<AdminWiki />} />
              <Route path="newsletter-builder" element={<AdminNewsletterBuilder />} />
              <Route path="web-vitals" element={<AdminWebVitals />} />
              <Route path="social-media" element={<AdminSocialMedia />} />
              <Route path="timesheets" element={<AdminTimesheets />} />
              <Route path="okrs" element={<AdminOKRs />} />
              <Route path="competitors" element={<AdminCompetitors />} />
              <Route path="sustainability" element={<AdminSustainability />} />
              <Route path="gdpr-full" element={<AdminGDPRFull />} />
              <Route path="finance-full" element={<AdminFinanceFull />} />
              <Route path="live-data" element={<AdminLiveData />} />
              <Route path="field-ops" element={<AdminFieldOps />} />
              <Route path="payroll" element={<AdminPayroll />} />
              <Route path="ecommerce" element={<AdminEcommerce />} />
              <Route path="lms" element={<AdminLMS />} />
              <Route path="contracts" element={<AdminContracts />} />
              <Route path="analytics-stream" element={<AdminAnalyticsStream />} />
              <Route path="system-dr" element={<AdminSystemDR />} />
              {/* V7 routes */}
              <Route path="entitlements" element={<AdminEntitlements />} />
              <Route path="corporate-seats" element={<AdminCorporateSeats />} />
              <Route path="ab-tests" element={<AdminHeadlineExperiments />} />
              <Route path="ai-governance" element={<AdminAIGovernance />} />
              <Route path="account-security" element={<AdminAccountSecurity />} />
              <Route path="governance" element={<AdminGovernance />} />
              {/* V8+ routes */}
              <Route path="wire-ingestion" element={<AdminWireIngestion />} />
              <Route path="social-publishing" element={<AdminSocialPublishing />} />
              <Route path="elections" element={<AdminElections />} />
            </Route>

            {/* 404 Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="bottom-right" richColors />
          <CookieConsent />
          <LiveChatWidget />
        </div>
      </Router>
      </I18nProvider>
      </EditionProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
